import { createSlice } from '@reduxjs/toolkit';

import { CONNECTION_STATE_DATA } from '../Actions/Constants';
import { CONNECTION_STATE_CONNECTED } from '../Helpers/Chat/Constant';
import { clearState } from './clearSlice';

// The live poll is kept apart from the message, whose body is a snapshot.
const initialState = {
    byId: {},
    staleIds: {},
    labelsByChat: {},
    // Kept outside the virtualised thread, which unmounts a card that moves out of view.
    openDialog: null,
};

const pollSlice = createSlice({
    name: 'poll',
    initialState,
    reducers: {
        // An answer older than the copy already held is dropped, so events stay in order.
        pollsReceived(state, action) {
            (action.payload || []).forEach(poll => {
                if (!poll?.pollId) return;
                const held = state.byId[poll.pollId];
                if (held && Number(held.version) > Number(poll.version)) return;
                state.byId[poll.pollId] = poll;
                delete state.staleIds[poll.pollId];
            });
        },
        pollsMarkedStale(state, action) {
            (action.payload || []).forEach(pollId => {
                state.staleIds[pollId] = true;
            });
        },
        pollLabelsReceived(state, action) {
            const { chatId, labels = [] } = action.payload || {};
            if (!chatId) return;
            const held = state.labelsByChat[chatId] || {};
            labels.forEach(label => {
                if (label?.activityId) held[label.activityId] = label;
            });
            state.labelsByChat[chatId] = held;
        },
        pollDialogOpened(state, action) {
            const { chatId, msgId } = action.payload || {};
            state.openDialog = chatId && msgId ? { chatId, msgId } : null;
        },
        pollDialogClosed(state) {
            state.openDialog = null;
        },
    },
    extraReducers: builder => {
        builder.addCase(clearState, () => initialState);
        // Events missed while offline are replayed, but a reload is the only sure way back.
        builder.addMatcher(
            action => action.type === CONNECTION_STATE_DATA && action.payload?.data === CONNECTION_STATE_CONNECTED,
            state => {
                Object.keys(state.byId).forEach(pollId => {
                    state.staleIds[pollId] = true;
                });
            },
        );
    },
});

export const { pollsReceived, pollsMarkedStale, pollLabelsReceived, pollDialogOpened, pollDialogClosed } =
    pollSlice.actions;

export const selectOpenPollDialog = state => state.poll.openDialog;

export const selectPoll = (state, pollId) => state.poll.byId[pollId];

export const selectIsPollStale = (state, pollId) => Boolean(state.poll.staleIds[pollId]);

const NO_LABELS = {};

export const selectChatPollLabels = (state, chatId) => state.poll.labelsByChat[chatId] || NO_LABELS;

export default pollSlice.reducer;
